import type { TOrderType, TOrderTableColumns, TOrder } from '@entities/orders';
import THeadToggler from '@features/table-thead-toggler';
import ErrorMessage from '@shared/components/ErrorMessage';
import Loading from '@shared/components/Loading';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { Pagination, PaginationRows } from '@shared/components/Pagination';
import { Table, THead, TBody, TableContainer, TableProvider } from '@shared/components/Table';

import { ApproveOrderKRW } from './components/ApproveOrderKRW';
import { getMutationHook } from './settings/getMutationHook';
import { useTableSettings } from './settings/useTableSettings';

interface IProps {
  type: TOrderType;
}

const OrdersWidget = ({ type }: IProps) => {
  const useMutationHook = getMutationHook(type);

  const {
    orders,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,

    filter,
  } = useMutationHook();

  const {
    columnTitles,
    renderFilterElement,
    getTheadBefore,
    getTheadAfter,
    getTbodyBefore,
    getTbodyAfter,
    renderRow,
  } = useTableSettings({
    type,
    refetch,
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorMessage error={error} />;

  if (orders) {
    return (
      <>
        {isFetching && <Loading />}

        <TableProvider<TOrderTableColumns>
          columnTitles={columnTitles}
          storageKey={`order-${type}`}
          {...filter}
        >
          <THeadToggler />

          <TableContainer className='table-docked-controls'>
            <Table>
              <THead
                items={orders.data as TOrder[]}
                renderItem={renderFilterElement}
                beforeColumns={getTheadBefore}
                afterColumns={getTheadAfter}
              />
              <TBody
                items={orders.data as TOrder[]}
                renderItem={renderRow}
                beforeColumns={getTbodyBefore}
                afterColumns={getTbodyAfter}
              />
            </Table>
          </TableContainer>

          <div className='pagination-container'>
            <Pagination data={orders.meta} />
            <PaginationRows />
          </div>
        </TableProvider>
      </>
    );
  }

  return null;
};

export { ApproveOrderKRW };

export default OrdersWidget;
